import { isNil } from "lodash";

import { NO_DATA_SIGN } from "app/filters/const";

import {
    getKeywordsData,
    formatChartData,
    processRankPositionData,
    getProgressBarValue,
} from "../../../helpers/helpers";
import type {
    GraphPoint,
    SearchTrafficDataSchema,
    SearchKeywordsDataSchema,
    SearchBrandedSplitDataSchema,
    SearchRankDistributionDataSchema,
} from "../../../services/search-overview-api/types";
import type { ESourceType } from "../../../types";
import type { SearchMetricData } from "../types";
import { SearchMetricName } from "../types";
import { getSearchWidgetsConfig } from "../widgetConfig";

import { costPerVisitFormatter, parseGraphData } from "./utils";

const createGraphData = (graph: GraphPoint[]) => {
    const parsedGraph = parseGraphData(graph);
    return {
        data: parsedGraph,
        isEmpty: parsedGraph.length < 2,
    };
};

export const transformTrafficData = (
    trafficData: SearchTrafficDataSchema,
    dispatch: React.Dispatch<{ payload: Partial<SearchMetricData> }>,
) => {
    if (!trafficData) {
        dispatch({
            payload: {
                [SearchMetricName.ALL]: null,
                [SearchMetricName.PAID]: null,
                [SearchMetricName.ORGANIC]: null,
                [SearchMetricName.PPC]: null,
            },
        });
        return;
    }
    const result: Partial<SearchMetricData> = {};
    const searchWidgetsConfig = getSearchWidgetsConfig();
    if (trafficData.TrafficSourcesVisits) {
        Object.entries(trafficData.TrafficSourcesVisits).forEach(([domain, metrics]) => {
            if (!metrics) {
                return;
            }
            Object.entries(metrics).forEach(([metricName, metricData]) => {
                const widgetName = metricName.toLowerCase();
                const graph = createGraphData(metricData?.Graph);
                result[widgetName] = result[widgetName] || {};
                const title =
                    !isNil(metricData?.Average) && searchWidgetsConfig[widgetName]
                        ? searchWidgetsConfig[widgetName].valueFormatter(metricData.Average)
                        : NO_DATA_SIGN;

                result[widgetName][domain] = {
                    domain,
                    graph,
                    title,
                    change: metricData?.YoyChange,
                    totalVisits: metricData?.TotalVisits,
                    description: getProgressBarValue(metricData?.Total, metricData?.TotalVisits),
                };
            });
        });
    }
    if (trafficData.AdSpend) {
        Object.entries(trafficData.AdSpend).forEach(([domain, data]) => {
            const graph = createGraphData(data?.PayPerClick?.Graph);
            result.ppcSpend = result.ppcSpend || {};
            const costPerVisit = data?.CostPerVisit?.CostPerVisit;
            const formattedValue = costPerVisitFormatter(costPerVisit);
            result.ppcSpend[domain] = {
                domain,
                graph,
                title: searchWidgetsConfig[SearchMetricName.PPC].valueFormatter(data?.PayPerClick?.Average),
                change: data?.PayPerClick?.YoyChange,
                description: {
                    formattedValue,
                    numericValue: costPerVisit,
                },
            };
        });
    }
    dispatch({ payload: result });
};

export const transformKeywordsData = (
    data: SearchKeywordsDataSchema,
    sourceType: ESourceType.ORGANIC | ESourceType.PAID,
    dispatch: React.Dispatch<{ payload: Partial<SearchMetricData> }>,
) => {
    if (!data) {
        return;
    }
    const result = Object.entries(data).reduce((acc, [domain, value]) => {
        if (!value) {
            return acc;
        }

        acc[sourceType] = acc[sourceType] || {};

        acc[sourceType][domain] = {
            domain,
            keywordsData: getKeywordsData(value),
        };
        return acc;
    }, {});
    dispatch({ payload: result });
};

export const transformBrandedSplitData = (
    data: SearchBrandedSplitDataSchema,
    dispatch: React.Dispatch<{ payload: Partial<SearchMetricData> }>,
) => {
    if (!data) {
        dispatch({ payload: { [SearchMetricName.BRANDED]: null } });
        return;
    }
    const result = Object.entries(data).reduce((acc, [domain, value]) => {
        if (!value) {
            return acc;
        }

        acc.branded = acc.branded || {};
        acc.branded[domain] = {
            domain,
            chartData: formatChartData(value),
            isEmpty: value.BrandedShare === 0 || value.NonBrandedShare === 0,
        };

        return acc;
    }, {} as Partial<SearchMetricData>);

    dispatch({ payload: result });
};
export const transformRankData = (
    data: SearchRankDistributionDataSchema,
    dispatch: React.Dispatch<{ payload: Partial<SearchMetricData> }>,
) => {
    if (!data?.RankSplit) {
        dispatch({ payload: { [SearchMetricName.RANK_POSITION]: null } });
        return;
    }
    const country = data?.Country;
    const result: Partial<SearchMetricData> = {};
    Object.entries(data.RankSplit).forEach(([domain, domainData]) => {
        const res = processRankPositionData(domainData);
        result.rankPosition = result.rankPosition || {};
        result.rankPosition[domain] = {
            domain,
            title: res?.keywords,
            change: res?.change,
            description: res?.percentageOf,
            country,
        };
    });

    dispatch({ payload: result });
};
